import * as z from "zod";

import { LabelSchema, sortLabel } from "./label-model";

export const LabelInstanceSchema = z.object({
  id: z.string(),
  labelId: z.string(),
  labelGroupId: z.string().optional().nullable(),
  ownerId: z.string(),
  ownerType: z.string(),
  workspaceId: z.string(),
  label: LabelSchema,
  labelGroup: z
    .object({
      id: z.string(),
      title: z.string(),
      color: z.string(),
    })
    .optional()
    .nullable(),
});
export type LabelInstance = z.infer<typeof LabelInstanceSchema>;

/**
 * Sorts two LabelInstance objects.
 * @param a First LabelInstance
 * @param b Second LabelInstance
 * @param desc Whether to sort in descending order
 * @returns -1, 0, or 1 based on the comparison
 */
export const sortLabelInstance = (
  a?: LabelInstance | null | undefined,
  b?: LabelInstance | null | undefined,
  desc: boolean = false,
): 0 | 1 | -1 => {
  if (!a && !b) {
    return 0;
  }

  if (!a) {
    return desc ? 1 : -1;
  }

  if (!b) {
    return desc ? -1 : 1;
  }

  return sortLabel(a.label, b.label, desc);
};

/**
 * Sorts two lists of LabelInstance objects.
 * The alphabetically first label in each list is used to determine the sorting order.
 * @param listA First list of LabelInstance objects
 * @param listB Second list of LabelInstance objects
 * @param desc Whether to sort in descending order
 * @returns -1, 0, or 1 based on the comparison
 */
export const sortLabelInstanceLists = (
  listA: LabelInstance[] = [],
  listB: LabelInstance[] = [],
  desc: boolean = false,
): 0 | 1 | -1 => {
  if (listA.length === 0 && listB.length === 0) {
    return 0;
  }

  if (listA.length === 0) {
    return desc ? 1 : -1;
  }

  if (listB.length === 0) {
    return desc ? -1 : 1;
  }

  // Sort both lists alphabetically by Label name
  const sortedA = [...listA].sort((a, b) => sortLabelInstance(a, b));
  const sortedB = [...listB].sort((a, b) => sortLabelInstance(a, b));

  // Compare the first elements of the sorted lists
  return sortLabelInstance(sortedA[0], sortedB[0], desc);
};
